import { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { PlayerAvatar } from "./PlayerAvatar";
import { MatchdayExportCard } from "./MatchdayExportCard";
import { EditDateDialog } from "./EditDateDialog";
import { VoteDialog } from "./VoteDialog";
import { setsSummary } from "./helpers";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Loader2, CheckCircle2, CircleDashed, Download, Share2, CalendarDays, MessageCircle, Sparkles, RefreshCw, Star } from "lucide-react";

function MatchVotesSummary({ match, entry, playersMap }) {
  if (!entry) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`match-votes-summary-${match.court}`}>
      {[...match.team1, ...match.team2].map((pid) => {
        const a = entry.averages[pid];
        if (!a) return null;
        return (
          <span key={pid} className="px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-[11px] text-amber-200">
            {playersMap[pid]?.short_name} <b className="font-mono">{a.avg.toFixed(1)}</b>
          </span>
        );
      })}
    </div>
  );
}

function names(ids, map) {
  return ids.map((id) => map[id]?.short_name).join(" / ");
}

function ResultEditor({ match, playersMap, onClose, onSaved }) {
  const init = match.sets?.length ? match.sets.map((s) => ({ ...s })) : [{ g1: "", g2: "" }, { g1: "", g2: "" }];
  const [sets, setSets] = useState(init);
  const [dateOverride, setDateOverride] = useState(match.date_override || "");
  const [notes, setNotes] = useState(match.notes || "");
  const [saving, setSaving] = useState(false);

  const update = (i, key, val) => {
    const copy = sets.map((s) => ({ ...s }));
    copy[i][key] = val;
    setSets(copy);
  };

  const save = async () => {
    const clean = sets
      .filter((s) => s.g1 !== "" && s.g2 !== "")
      .map((s) => ({ g1: parseInt(s.g1, 10), g2: parseInt(s.g2, 10) }));
    if (clean.some((s) => isNaN(s.g1) || isNaN(s.g2))) {
      toast.error("Inserisci punteggi validi");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/matches/${match.id}`, {
        sets: clean,
        date_override: dateOverride || null,
        notes: notes || null,
      });
      toast.success("Risultato salvato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const clearResult = async () => {
    setSaving(true);
    try {
      await api.delete(`/matches/${match.id}/result`);
      toast.success("Risultato cancellato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="bg-slate-900/95 backdrop-blur-xl border border-white/10 text-slate-100 max-w-md" data-testid="result-editor-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-xl">
          Campo {match.court} — Risultato
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 pt-1">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800">
            <PlayerAvatar id={match.team1[0]} name={playersMap[match.team1[0]]?.name} size={26} ring={false} />
            <span className="font-semibold text-white truncate">{names(match.team1, playersMap)}</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800">
            <PlayerAvatar id={match.team2[0]} name={playersMap[match.team2[0]]?.name} size={26} ring={false} />
            <span className="font-semibold text-white truncate">{names(match.team2, playersMap)}</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400">Punteggio set (games)</Label>
          {sets.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-500 w-12">Set {i + 1}</span>
              <Input
                data-testid={`set-${i + 1}-team1-input`}
                type="number"
                min="0"
                value={s.g1}
                onChange={(e) => update(i, "g1", e.target.value)}
                className="bg-slate-900 border-slate-700 text-center font-mono focus-visible:ring-emerald-500"
              />
              <span className="text-slate-600">-</span>
              <Input
                data-testid={`set-${i + 1}-team2-input`}
                type="number"
                min="0"
                value={s.g2}
                onChange={(e) => update(i, "g2", e.target.value)}
                className="bg-slate-900 border-slate-700 text-center font-mono focus-visible:ring-emerald-500"
              />
            </div>
          ))}
          {sets.length < 3 && (
            <Button
              data-testid="add-set-btn"
              variant="ghost"
              size="sm"
              onClick={() => setSets([...sets, { g1: "", g2: "" }])}
              className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-800/60"
            >
              <Plus className="w-4 h-4 mr-1" /> Aggiungi set
            </Button>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-400">Data (se posticipata)</Label>
          <Input
            data-testid="date-override-input"
            type="date"
            value={dateOverride}
            onChange={(e) => setDateOverride(e.target.value)}
            className="bg-slate-900 border-slate-700 font-mono focus-visible:ring-emerald-500"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-400">Note (sostituti, accordi)</Label>
          <Textarea
            data-testid="notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="bg-slate-900 border-slate-700 focus-visible:ring-emerald-500 resize-none"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            data-testid="match-result-submit-btn"
            onClick={save}
            disabled={saving}
            className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase tracking-wide"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
          </Button>
          {match.status === "completed" && (
            <Button
              data-testid="clear-result-btn"
              onClick={clearResult}
              disabled={saving}
              variant="ghost"
              className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 border border-rose-500/30"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </DialogContent>
  );
}

export function ResultsView({ matchdays, playersMap, isAdmin, onSaved }) {
  const [selected, setSelected] = useState(matchdays[0]?.number || 1);
  const [editDate, setEditDate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const exportRef = useRef(null);
  const md = matchdays.find((m) => m.number === selected) || matchdays[0];
  const hasResults = md?.matches.some((m) => m.status === "completed");
  const [votes, setVotes] = useState({});
  const [voting, setVoting] = useState(null);
  const mdNumber = md?.number;
  const loadVotes = useCallback(() => {
    if (!mdNumber) return;
    api.get("/votes", { params: { matchday: mdNumber } }).then((r) => setVotes(r.data)).catch(() => setVotes({}));
  }, [mdNumber]);
  useEffect(() => { loadVotes(); }, [loadVotes]);

  const loadReport = async (refresh = false) => {
    if (!md) return;
    setReportLoading(true);
    setReport(null);
    try {
      const { data } = await api.get(`/matchday/${md.number}/report${refresh ? "?refresh=true" : ""}`);
      setReport(data.has_report ? data.report : "");
    } catch {
      setReport("");
    } finally {
      setReportLoading(false);
    }
  };

  const renderCanvas = async () => {
    if (!exportRef.current) return null;
    return html2canvas(exportRef.current, { backgroundColor: "#090D16", scale: 2, logging: false });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const canvas = await renderCanvas();
      const link = document.createElement("a");
      link.download = `giornata-${md.number}-american-padel.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const canvas = await renderCanvas();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `giornata-${md.number}-american-padel.png`, { type: "image/png" });
      const canShareFiles =
        typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: `Giornata ${md.number} — American Padel 2026`,
            text: "Risultati della giornata 🎾",
          });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `giornata-${md.number}-american-padel.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Immagine salvata (condivisione diretta non disponibile)");
    } catch (e) {
      toast.error("Errore durante la condivisione");
    } finally {
      setExporting(false);
    }
  };

  const fmtLong = (d) => {
    if (!d) return "Data da definire";
    try {
      return new Date(d).toLocaleDateString("it-IT", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch { return d; }
  };
  const fmtShort = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    } catch { return d; }
  };

  const copyWhatsapp = async () => {
    if (!md) return;
    const lines = [`🎾 *American Padel 2026* — Giornata ${md.number}`, fmtLong(md.date), ""];
    md.matches
      .slice()
      .sort((a, b) => a.court - b.court)
      .forEach((m) => {
        const sets = m.sets || [];
        const s1 = sets.filter((s) => s.g1 > s.g2).length;
        const s2 = sets.filter((s) => s.g2 > s.g1).length;
        const t1 = names(m.team1, playersMap);
        const t2 = names(m.team2, playersMap);
        if (m.status === "completed") {
          lines.push(`🏟️ Campo ${m.court}: ${t1} ${s1}-${s2} ${t2}  (${setsSummary(sets)})`);
        } else {
          lines.push(`🏟️ Campo ${m.court}: ${t1} vs ${t2} — da giocare`);
        }
      });
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Riepilogo copiato! Incollalo nel gruppo WhatsApp");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <div className="space-y-5" data-testid="results-view">
      <MatchdayExportCard ref={exportRef} matchday={md} playersMap={playersMap} votes={votes} />
      {isAdmin && <EditDateDialog matchday={md} open={editDate} onOpenChange={setEditDate} onSaved={onSaved} />}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            Risultati & Inserimento
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white">
            Risultati per Giornata
          </h2>
          {md && (
            <div
              data-testid="selected-matchday-date"
              className="mt-1.5 flex items-center gap-2 text-slate-300 text-sm capitalize"
            >
              <CalendarDays className="w-4 h-4 text-emerald-400" />
              Giornata {md.number} · {fmtLong(md.date)}
              {(md.time || md.matches?.[0]?.time) && <span data-testid="selected-matchday-time" className="normal-case"> · ore {md.time || md.matches[0].time}</span>}
              {isAdmin && (
                <button
                  data-testid="edit-matchday-date-btn"
                  onClick={() => setEditDate(true)}
                  title="Cambia data"
                  className="ml-1 p-1 rounded-md text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="whatsapp-recap-btn"
            onClick={copyWhatsapp}
            disabled={!md}
            size="sm"
            className="bg-[#25D366] hover:bg-[#1fb855] text-slate-950 font-bold uppercase text-xs tracking-wide"
          >
            <MessageCircle className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">WhatsApp</span>
          </Button>
          <Button
            data-testid="export-matchday-btn"
            onClick={handleDownload}
            disabled={exporting || !md}
            size="sm"
            className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 font-semibold uppercase text-xs tracking-wide"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 sm:mr-1.5" />}
            <span className="hidden sm:inline">Scarica</span>
          </Button>
          <Button
            data-testid="share-matchday-btn"
            onClick={handleShare}
            disabled={exporting || !md}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs tracking-wide"
          >
            <Share2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Condividi</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {matchdays.map((m) => (
          <button
            key={m.number}
            data-testid={`matchday-pill-${m.number}`}
            onClick={() => { setSelected(m.number); setReport(null); }}
            className={`px-4 py-1.5 rounded-xl font-display transition-colors text-center leading-tight ${
              m.number === selected
                ? "bg-emerald-500 text-slate-950"
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            <span className="block font-bold uppercase text-sm tracking-wide">Giornata {m.number}</span>
            <span className={`block text-[10px] font-mono ${m.number === selected ? "text-slate-800" : "text-slate-500"}`}>
              {fmtShort(m.date)}
            </span>
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {md?.matches
          .slice()
          .sort((a, b) => a.court - b.court)
          .map((m) => {
            const sets = m.sets || [];
            const s1 = sets.filter((s) => s.g1 > s.g2).length;
            const s2 = sets.filter((s) => s.g2 > s.g1).length;
            const done = m.status === "completed";
            return (
              <div
                key={m.id}
                data-testid={`result-card-${m.court}`}
                className="ap-card p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold uppercase text-white">Campo {m.court}</span>
                  {done ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-400 font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Completata
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-amber-400 font-semibold">
                      <CircleDashed className="w-3.5 h-3.5" /> Da giocare
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex -space-x-2">
                        {m.team1.map((id) => (
                          <PlayerAvatar key={id} id={id} name={playersMap[id]?.name} size={28} />
                        ))}
                      </div>
                      <span className={`text-sm truncate ${done && s1 > s2 ? "font-bold text-white" : "text-slate-300"}`}>
                        {names(m.team1, playersMap)}
                      </span>
                    </div>
                    {done && <span className="font-mono font-black text-emerald-400">{s1}</span>}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex -space-x-2">
                        {m.team2.map((id) => (
                          <PlayerAvatar key={id} id={id} name={playersMap[id]?.name} size={28} />
                        ))}
                      </div>
                      <span className={`text-sm truncate ${done && s2 > s1 ? "font-bold text-white" : "text-slate-300"}`}>
                        {names(m.team2, playersMap)}
                      </span>
                    </div>
                    {done && <span className="font-mono font-black text-emerald-400">{s2}</span>}
                  </div>
                </div>

                <div className="text-xs font-mono text-slate-500 min-h-4">{setsSummary(sets)}</div>
                {m.notes && <p className="text-[11px] text-slate-500 italic">{m.notes}</p>}

                {done && <MatchVotesSummary match={m} entry={votes[m.id]} playersMap={playersMap} />}
                {done && (
                  <Button
                    data-testid={`vote-match-btn-${m.court}`}
                    onClick={() => setVoting(m)}
                    size="sm"
                    className="w-full bg-amber-400/90 hover:bg-amber-400 text-slate-950 font-bold uppercase text-xs tracking-wide"
                  >
                    <Star className="w-3.5 h-3.5 mr-1.5" />
                    Voti ({votes[m.id]?.votes.length || 0}/4)
                  </Button>
                )}

                {isAdmin && (
                  <Button
                    data-testid={`edit-match-btn-${m.court}`}
                    onClick={() => setEditing(m)}
                    size="sm"
                    className="w-full bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 font-semibold uppercase text-xs tracking-wide"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    {done ? "Modifica" : "Inserisci risultato"}
                  </Button>
                )}
              </div>
            );
          })}
      </div>

      {hasResults && (
        <div className="pt-1" data-testid="matchday-report-section">
          {!report && !reportLoading ? (
            <Button
              data-testid="load-report-btn"
              onClick={() => loadReport(false)}
              size="sm"
              className="bg-fuchsia-500/90 hover:bg-fuchsia-500 text-white font-bold uppercase text-xs tracking-wide"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Report AI della giornata
            </Button>
          ) : (
            <div className="ap-glass rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-fuchsia-400" />
                  <span className="font-display uppercase tracking-wide text-sm text-slate-300">
                    Report AI · Giornata {md.number}
                  </span>
                </div>
                {report && (
                  <button
                    data-testid="report-refresh-btn"
                    onClick={() => loadReport(true)}
                    className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400 hover:text-fuchsia-400"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${reportLoading ? "animate-spin" : ""}`} /> Rigenera
                  </button>
                )}
              </div>
              {reportLoading ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-fuchsia-400" /> Generazione cronaca in corso…
                </div>
              ) : (
                <p className="text-sm text-slate-200 leading-relaxed">{report}</p>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!voting} onOpenChange={(o) => !o && setVoting(null)}>
        {voting && (
          <VoteDialog
            match={voting}
            entry={votes[voting.id]}
            playersMap={playersMap}
            isAdmin={isAdmin}
            onSaved={loadVotes}
          />
        )}
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <ResultEditor
            match={editing}
            playersMap={playersMap}
            onClose={() => setEditing(null)}
            onSaved={onSaved}
          />
        )}
      </Dialog>
    </div>
  );
}
